import { useState, useEffect, useCallback } from 'react';
import { tarjetaControlApi, trabajadoresApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { SearchableSelect } from '../../components/SearchableSelect';
import { FormSkeleton } from '../../lib/skeletons';
import { toast } from '../../hooks/use-toast';
import { Save, Loader2, Clock, CalendarDays, Zap, Users } from 'lucide-react';

const TIPO_OPTIONS = [
  { value: 'N', label: 'Normal', color: '' },
  { value: 'P', label: 'Permiso', color: 'bg-blue-500/10 text-blue-600' },
  { value: 'L', label: 'Licencia', color: 'bg-purple-500/10 text-purple-600' },
  { value: 'V', label: 'Vacaciones', color: 'bg-cyan-500/10 text-cyan-600' },
  { value: 'F', label: 'Falla', color: 'bg-red-500/10 text-red-600' },
  { value: 'O', label: 'Otro', color: 'bg-orange-500/10 text-orange-600' },
  { value: 'T', label: 'Trato', color: 'bg-yellow-500/10 text-yellow-700' },
  { value: 'FE', label: 'Feriado', color: 'bg-rose-500/10 text-rose-600' },
  { value: 'Q', label: 'Finiquito', color: 'bg-stone-500/10 text-stone-600' },
  { value: 'X', label: 'Fin de Semana', color: 'bg-muted text-muted-foreground' },
];

const DOW_NAMES = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];

function getDow(year, month, day) {
  return new Date(year, month - 1, day).getDay(); // 0=Sun
}

export default function TarjetaControlPage() {
  const now = new Date();
  const [mes, setMes] = useState(`${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getFullYear()).slice(2)}`);
  const [workerNum, setWorkerNum] = useState(null);
  const [allWorkers, setAllWorkers] = useState([]);
  const [tarjeta, setTarjeta] = useState(null);
  const [dias, setDias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const workerOptions = allWorkers.map(w => ({
    value: w.numero, label: w.nombre, prefix: `${w.numero}`,
    secondary: `${w.nombre} ${w.cargo || ''} ${w.numero}`,
  }));

  useEffect(() => {
    trabajadoresApi.getAll({ activo: true }).then(r => setAllWorkers(r.data)).catch(() => {});
  }, []);  

  const loadTarjeta = useCallback(async () => {
    if (!workerNum || !mes) return;
    setLoading(true);
    try {
      const res = await tarjetaControlApi.get({ trabajador_numero: workerNum, mes });
      setTarjeta(res.data);
      setDias(res.data.dias || []);
    } catch {
      toast({ title: 'Error al cargar tarjeta', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [workerNum, mes]);

  useEffect(() => { if (workerNum && mes) loadTarjeta(); }, [loadTarjeta, workerNum, mes]);

  const handleApplyStandard = () => {
    // Find the next week (Mon-Fri) that has empty time entries
    const mesParts2 = mes.split('/');
    const mm = parseInt(mesParts2[0]);
    const yy = 2000 + parseInt(mesParts2[1]);

    setDias(prev => {
      const updated = [...prev];
      // Find first weekday without entrada_am
      let startIdx = -1;
      for (let i = 0; i < updated.length; i++) {
        const d = updated[i];
        const dow = new Date(yy, mm - 1, d.dia).getDay(); // 0=Sun
        if (dow >= 1 && dow <= 5 && !d.entrada_am && d.tipo !== 'P' && d.tipo !== 'L' && d.tipo !== 'V' && d.tipo !== 'F' && d.tipo !== 'O' && d.tipo !== 'FE') {
          startIdx = i;
          break;
        }
      }
      if (startIdx === -1) {
        toast({ title: 'Todas las semanas ya tienen horario' });
        return prev;
      }

      // Fill from startIdx until end of that week (or next weekend)
      let filled = 0;
      for (let i = startIdx; i < updated.length; i++) {
        const d = updated[i];
        const dow = new Date(yy, mm - 1, d.dia).getDay();
        if (dow === 0 || dow === 6) {
          if (filled > 0) break; // Already filled some days, stop at weekend
          continue;
        }
        if (d.tipo === 'P' || d.tipo === 'L' || d.tipo === 'V' || d.tipo === 'F' || d.tipo === 'O' || d.tipo === 'FE') continue;
        updated[i] = {
          ...d,
          entrada_am: '08:00', salida_am: '13:00',
          entrada_pm: '14:00', salida_pm: '18:00',
          tipo: 'N',
        };
        filled++;
        // Stop after Friday (dow=5)
        if (dow === 5) break;
      }

      if (filled > 0) {
        toast({ title: `Semana llenada: ${filled} dias con horario estandar` });
      }
      return updated;
    });
  };

  const updateDia = (idx, field, value) => {
    setDias(prev => {
      const u = [...prev];
      u[idx] = { ...u[idx], [field]: value };
      return u;
    });
  };

  const handleSave = async () => {
    if (!workerNum || !mes) return;
    setSaving(true);
    try {
      const res = await tarjetaControlApi.save({
        trabajador_numero: workerNum,
        mes,
        dias: dias.map(d => ({
          dia: d.dia,
          entrada_am: d.entrada_am || '',
          salida_am: d.salida_am || '',
          entrada_pm: d.entrada_pm || '',
          salida_pm: d.salida_pm || '',
          tipo: d.tipo || 'N',
        })),
      });
      setTarjeta(res.data);
      setDias(res.data.dias || []);
      toast({ title: `Tarjeta guardada: ${res.data.total_horas}h (${res.data.dias_trabajados} dias)` });
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Error al guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleMonthChange = (e) => {
    const v = e.target.value; // YYYY-MM
    if (v) {
      const [y, m] = v.split('-');
      setMes(`${m}/${y.slice(2)}`);
    }
  };

  // Calc totals from current dias
  const calcTotals = () => {
    let hn = 0, hex = 0, worked = 0;
    for (const d of dias) {
      if (d.tipo !== 'N') continue;
      const am = calcH(d.entrada_am, d.salida_am);
      const pm = calcH(d.entrada_pm, d.salida_pm);
      const total = am + pm;
      if (total > 0) {
        hn += Math.min(total, 9);
        hex += Math.max(0, total - 9);
        worked++;
      }
    }
    return { hn: hn.toFixed(1), hex: hex.toFixed(1), total: (hn + hex).toFixed(1), worked };
  };

  function calcH(e, s) {
    if (!e || !s) return 0;
    const [h1, m1] = e.split(':').map(Number);
    const [h2, m2] = s.split(':').map(Number);
    return Math.max(0, (h2 * 60 + m2 - h1 * 60 - m1) / 60);
  }

  const totals = dias.length > 0 ? calcTotals() : { hn: '0', hex: '0', total: '0', worked: 0 };

  // Parse mes for display
  const mesParts = mes.split('/');
  const mesMonth = parseInt(mesParts[0]);
  const mesYear = 2000 + parseInt(mesParts[1] || '26');

  return (
    <div className="space-y-4 page-enter" data-testid="tarjeta-control">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Tarjeta de Control</h1>
        <p className="text-sm text-muted-foreground">Ingreso mensual de marcas del reloj control</p>
      </div>

      {/* Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5 w-72">
              <Label className="text-xs">Trabajador</Label>
              <SearchableSelect
                value={workerNum}
                onChange={(v) => setWorkerNum(v)}
                options={workerOptions}
                placeholder="Buscar trabajador..."
                data-testid="select-worker"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Mes</Label>
              <input type="month" onChange={handleMonthChange} className="h-9 rounded-xl border bg-background px-3 text-sm" data-testid="select-mes" />
            </div>
            {workerNum && dias.length > 0 && (
              <Button variant="outline" onClick={handleApplyStandard} className="rounded-xl gap-1.5" data-testid="btn-apply-standard">
                <Zap className="h-4 w-4" /> Horario Estandar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {loading && <FormSkeleton fields={6} />}

      {/* Grid */}
      {!loading && dias.length > 0 && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="p-3 flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-primary/10"><Clock className="h-4 w-4 text-primary" /></div>
              <div><p className="text-[10px] text-muted-foreground">Total HH</p><p className="text-base font-bold tabular-nums">{totals.total}</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-3 flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-blue-500/10"><Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" /></div>
              <div><p className="text-[10px] text-muted-foreground">HN</p><p className="text-base font-bold tabular-nums">{totals.hn}</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-3 flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-amber-500/10"><Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" /></div>
              <div><p className="text-[10px] text-muted-foreground">HEX</p><p className="text-base font-bold tabular-nums">{totals.hex}</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-3 flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-emerald-500/10"><CalendarDays className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /></div>
              <div><p className="text-[10px] text-muted-foreground">Dias</p><p className="text-base font-bold tabular-nums">{totals.worked}</p></div>
            </CardContent></Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {tarjeta?.trabajador_nombre || ''} — {mes}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-2 py-1.5 text-left w-10">Dia</th>
                      <th className="px-1 py-1.5 text-center w-8">Sem</th>
                      <th className="px-1 py-1.5 text-center w-20">Tipo</th>
                      <th className="px-1 py-1.5 text-center w-[72px]">Ent. AM</th>
                      <th className="px-1 py-1.5 text-center w-[72px]">Sal. AM</th>
                      <th className="px-1 py-1.5 text-center w-[72px]">Ent. PM</th>
                      <th className="px-1 py-1.5 text-center w-[72px]">Sal. PM</th>
                      <th className="px-2 py-1.5 text-center w-14">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dias.map((d, idx) => {
                      const dow = getDow(mesYear, mesMonth, d.dia);
                      const dowIdx = dow === 0 ? 6 : dow - 1;
                      const isWeekend = dow === 0 || dow === 6;
                      const isNonWorking = d.tipo !== 'N';
                      const am = calcH(d.entrada_am, d.salida_am);
                      const pm = calcH(d.entrada_pm, d.salida_pm);
                      const dayTotal = (am + pm).toFixed(1);
                      const tipoInfo = TIPO_OPTIONS.find(t => t.value === d.tipo);

                      return (
                        <tr key={d.dia} className={`border-t transition-colors ${isWeekend ? 'bg-muted/20' : 'hover:bg-muted/10'} ${isNonWorking ? 'opacity-60' : ''}`} data-testid={`dia-${d.dia}`}>
                          <td className="px-2 py-0.5 font-medium tabular-nums">{d.dia}</td>
                          <td className={`px-1 py-0.5 text-center text-[10px] ${isWeekend ? 'text-red-500 font-bold' : 'text-muted-foreground'}`}>{DOW_NAMES[dowIdx]}</td>
                          <td className="px-0.5 py-0.5">
                            <select
                              className={`h-6 w-full rounded border bg-background px-1 text-[10px] ${tipoInfo?.color || ''}`}
                              value={d.tipo}
                              onChange={(e) => updateDia(idx, 'tipo', e.target.value)}
                              data-testid={`tipo-${d.dia}`}
                            >
                              {TIPO_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                          </td>
                          <td className="px-0.5 py-0.5"><Input type="time" value={d.entrada_am || ''} onChange={(e) => updateDia(idx, 'entrada_am', e.target.value)} disabled={isNonWorking} className="h-6 text-[11px] px-1 text-center rounded" /></td>
                          <td className="px-0.5 py-0.5"><Input type="time" value={d.salida_am || ''} onChange={(e) => updateDia(idx, 'salida_am', e.target.value)} disabled={isNonWorking} className="h-6 text-[11px] px-1 text-center rounded" /></td>
                          <td className="px-0.5 py-0.5"><Input type="time" value={d.entrada_pm || ''} onChange={(e) => updateDia(idx, 'entrada_pm', e.target.value)} disabled={isNonWorking} className="h-6 text-[11px] px-1 text-center rounded" /></td>
                          <td className="px-0.5 py-0.5"><Input type="time" value={d.salida_pm || ''} onChange={(e) => updateDia(idx, 'salida_pm', e.target.value)} disabled={isNonWorking} className="h-6 text-[11px] px-1 text-center rounded" /></td>
                          <td className={`px-2 py-0.5 text-center font-bold tabular-nums ${parseFloat(dayTotal) > 9 ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                            {isNonWorking ? '-' : dayTotal}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center gap-3">
            <div className="flex-1" />
            <Button onClick={handleSave} disabled={saving} className="rounded-xl gap-1.5" data-testid="btn-guardar-tarjeta">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar Tarjeta
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
